import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { proApi, errMsg, COUNTRIES } from "@/lib/proApi";
import { usePro } from "@/context/ProContext";

export const inputCls = "w-full rounded-xl border border-[var(--oz-border)] bg-white px-3.5 py-3 text-sm outline-none focus:border-[var(--oz-gold)]";

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold text-[var(--oz-text)] mb-1.5">{label}</span>
    {children}
  </label>
);

export const AddressFields = ({ value, onChange, prefix }) => {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <div className="sm:col-span-2"><Field label="Adresse *"><input required data-testid={`${prefix}-address`} value={value.address} onChange={set("address")} className={inputCls} /></Field></div>
      <Field label="Code postal *"><input required data-testid={`${prefix}-postal`} value={value.postal_code} onChange={set("postal_code")} className={inputCls} /></Field>
      <Field label="Ville *"><input required data-testid={`${prefix}-city`} value={value.city} onChange={set("city")} className={inputCls} /></Field>
      <div className="sm:col-span-2"><Field label="Pays *">
        <select data-testid={`${prefix}-country`} value={value.country} onChange={set("country")} className={inputCls}>
          {COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
        </select>
      </Field></div>
    </div>
  );
};

export const emptyAddr = { address: "", postal_code: "", city: "", country: "FR" };

const Card = ({ title, children, testid }) => (
  <div data-testid={testid} className="max-w-xl mx-auto px-4 py-12">
    <div className="rounded-3xl bg-white border border-[var(--oz-border)] p-6 sm:p-8">
      <h1 className="font-serif text-3xl sm:text-4xl text-[var(--oz-text)] mb-6">{title}</h1>
      {children}
    </div>
  </div>
);

const Err = ({ msg }) => msg ? <p data-testid="pro-auth-error" className="text-sm text-[var(--oz-badge)]">{msg}</p> : null;
const Submit = ({ busy, children, testid }) => (
  <button type="submit" disabled={busy} data-testid={testid} className="w-full inline-flex justify-center items-center gap-2 px-6 py-3.5 rounded-full bg-[var(--oz-dark)] text-white font-semibold disabled:opacity-60">
    {busy && <Loader2 className="w-4 h-4 animate-spin" />}{children}
  </button>
);

export function ProLogin() {
  const { signIn } = usePro();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await proApi.post("/pro/auth/login", f);
      signIn(data.token, data.user);
      nav(sp.get("next") || "/espace-pro");
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <Card title="Connexion professionnelle" testid="pro-login-page">
      <form onSubmit={submit} className="space-y-4">
        <Field label="E-mail"><input type="email" required data-testid="pro-login-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} /></Field>
        <Field label="Mot de passe"><input type="password" required data-testid="pro-login-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} /></Field>
        <Err msg={err} />
        <Submit busy={busy} testid="pro-login-submit">Se connecter</Submit>
      </form>
      <div className="mt-5 flex justify-between text-sm">
        <Link to="/espace-pro/mot-de-passe-oublie" data-testid="pro-forgot-link" className="text-[var(--oz-muted)] underline">Mot de passe oublié ?</Link>
        <Link to="/espace-pro/inscription" data-testid="pro-register-link" className="font-semibold text-[var(--oz-text)] underline">Créer un compte</Link>
      </div>
    </Card>
  );
}

export const ProfileFields = ({ f, setF }) => {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Prénom *"><input required data-testid="pro-reg-first" value={f.first_name} onChange={set("first_name")} className={inputCls} /></Field>
        <Field label="Nom *"><input required data-testid="pro-reg-last" value={f.last_name} onChange={set("last_name")} className={inputCls} /></Field>
        <div className="sm:col-span-2"><Field label="Nom de l'entreprise *"><input required data-testid="pro-reg-company" value={f.company_name} onChange={set("company_name")} className={inputCls} /></Field></div>
        <Field label="Téléphone *"><input required data-testid="pro-reg-phone" value={f.phone} onChange={set("phone")} className={inputCls} /></Field>
        <Field label="SIRET (si applicable)"><input data-testid="pro-reg-siret" inputMode="numeric" value={f.siret || ""} onChange={set("siret")} className={inputCls} placeholder="14 chiffres" /></Field>
        <div className="sm:col-span-2"><Field label="N° TVA intracommunautaire (si applicable)"><input data-testid="pro-reg-vat" value={f.vat_number || ""} onChange={set("vat_number")} className={inputCls} placeholder="FR…" /></Field></div>
      </div>
      <p className="pt-2 text-sm font-semibold">Adresse de facturation</p>
      <AddressFields prefix="pro-reg-billing" value={f.billing} onChange={(v) => setF({ ...f, billing: v, ...(f._same ? { shipping: v } : {}) })} />
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" data-testid="pro-reg-same-address" checked={!!f._same} onChange={(e) => setF({ ...f, _same: e.target.checked, shipping: e.target.checked ? f.billing : f.shipping })} className="w-4 h-4 accent-[var(--oz-gold)]" />
        Adresse de livraison identique
      </label>
      {!f._same && (<><p className="text-sm font-semibold">Adresse de livraison</p>
        <AddressFields prefix="pro-reg-shipping" value={f.shipping} onChange={(v) => setF({ ...f, shipping: v })} /></>)}
    </>
  );
};

export const profilePayload = (f) => {
  const { _same, email, password, id, status, created_at, ...rest } = f;
  return { ...rest, siret: rest.siret || null, vat_number: rest.vat_number || null };
};

export function ProRegister() {
  const { signIn } = usePro();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "", first_name: "", last_name: "", company_name: "", phone: "", siret: "", vat_number: "", billing: emptyAddr, shipping: emptyAddr, _same: true });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await proApi.post("/pro/auth/register", { ...profilePayload(f), email: f.email, password: f.password });
      signIn(data.token, data.user);
      nav("/espace-pro/compte");
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <Card title="Créer un compte professionnel" testid="pro-register-page">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="E-mail *"><input type="email" required data-testid="pro-reg-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} /></Field>
          <Field label="Mot de passe * (8 caractères min.)"><input type="password" required minLength={8} data-testid="pro-reg-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} /></Field>
        </div>
        <ProfileFields f={f} setF={setF} />
        <p className="text-xs text-[var(--oz-muted)]">Vos données servent à gérer votre compte, vos commandes et vos factures. Voir notre <Link to="/legal/confidentialite" className="underline">politique de confidentialité</Link>.</p>
        <Err msg={err} />
        <Submit busy={busy} testid="pro-register-submit">Créer mon compte</Submit>
      </form>
      <p className="mt-5 text-sm text-center">Déjà inscrit ? <Link to="/espace-pro/connexion" className="font-semibold underline">Se connecter</Link></p>
    </Card>
  );
}

export function ProForgot() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await proApi.post("/pro/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <Card title="Mot de passe oublié" testid="pro-forgot-page">
      {sent ? <p data-testid="pro-forgot-sent" className="text-sm text-[var(--oz-muted)]">Si un compte existe pour cette adresse, un e-mail avec un lien de réinitialisation (valable 1 heure) vient d'être envoyé.</p> : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="E-mail du compte"><input type="email" required data-testid="pro-forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} /></Field>
          <Err msg={err} />
          <Submit busy={busy} testid="pro-forgot-submit">Recevoir le lien</Submit>
        </form>
      )}
    </Card>
  );
}

export function ProReset() {
  const [sp] = useSearchParams();
  const [pw, setPw] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await proApi.post("/pro/auth/reset-password", { token: sp.get("token") || "", password: pw });
      setDone(true);
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <Card title="Nouveau mot de passe" testid="pro-reset-page">
      {done ? <p data-testid="pro-reset-done" className="text-sm">Mot de passe modifié. <Link to="/espace-pro/connexion" className="font-semibold underline">Se connecter</Link></p> : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nouveau mot de passe (8 caractères min.)"><input type="password" minLength={8} required data-testid="pro-reset-password" value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls} /></Field>
          <Err msg={err} />
          <Submit busy={busy} testid="pro-reset-submit">Enregistrer</Submit>
        </form>
      )}
    </Card>
  );
}
